import { computed, ref, watch, type Ref } from 'vue'
import { getPatentTrendData } from '@/infrastructure/api-functions';
import { E_ANALYSIS_CHART } from '@/types';
import { downloadImage } from '@eureka/utils/download-image';
import { useGlobalState } from './use-global-store';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useI18n } from 'vue-i18n';
import { generateImgTitle } from '@/infrastructure/utils/generate-img-title';

export function usePatentTrend(chartComponent: Ref<{
    generateChartImage: () => Promise<string>,
    drawChart: () => void,
    isChartLoaded: () => boolean
    isEmptyChart: boolean
} | undefined>) {
    const { t } = useI18n();

    const title = t('patentApplicationTrend.title');

    const { checkedChild, companyId } = useGlobalState();

    const chartKey = E_ANALYSIS_CHART.PATENT_TRENDS_BAR;

    const isFetchLoading = ref(false)

    const isDownloadingImage = ref(false)

    const disableUI = computed(() => {
        return (!chartComponent.value?.isChartLoaded && !isFetchLoading.value) || chartComponent.value?.isEmptyChart
    })

    function changeChild() {
        chartComponent.value?.drawChart()
    }

    function chartConfig() {
        return import('@/constant/chart/chartConfigTrend.json').then((d) => { return d.default });
    }

    function getPatentChartData() {
        isFetchLoading.value = true

        return getPatentTrendData({ child: checkedChild.value, company_id: companyId.value }).finally(
            () => {
                isFetchLoading.value = false
            }
        )
    }

    async function downloadChartAsImage() {
        if (isDownloadingImage.value) return
        isDownloadingImage.value = true
        try {
            const imageStr = await chartComponent.value?.generateChartImage()
            if (imageStr) {
                downloadImage(imageStr, generateImgTitle(title))
            }
        } finally {
            isDownloadingImage.value = false
        }
    }

    function handleDrillDown(data: Record<string, string>) {
        const { firstCode, secondCode } = data
        const dimensionList = [{
            field: 'APD_Y',
            value: firstCode,
        }]

        if (secondCode === 'B') {
            dimensionList.push({
                field: 'ISSUED',
                value: '1',
            })
        }
        openRightAside({
            title: t('patent'),
            data: {
                company_id: companyId.value,
                chart_type: chartKey,
                include_children: checkedChild.value,
                dimension_list: dimensionList,
            },
        })
    }

    watch(
        () => checkedChild.value,
        () => {
            chartComponent.value?.drawChart()
        }
    )

    return {
        title,
        disableUI,
        isFetchLoading,
        chartKey,
        chartConfig,
        getPatentChartData,
        changeChild,
        downloadChartAsImage,
        handleDrillDown,
    }
}
